<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { listProjetos } from '@/api/projetos'
import { deleteSessao, deleteSessoesLote, listSessoes } from '@/api/sessoes'
import { getApiErrorMessage } from '@/api/client'
import { useReindexAcoesJob } from '@/composables/useReindexAcoesJob'
import type { Projeto } from '@/types/projetos'
import type { SessaoGravacao, SessaoGravacaoStatus } from '@/types/sessoes'
import { formatDateTime } from '@/utils/datetime'
import {
  formatRetentionShort,
  formatSessaoBytes,
  formatSessaoDuration,
  sessaoDurationMs,
} from '@/utils/sessoes'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const PAGE_SIZE = 25
const METADATA_QUERY_PREFIX = 'md.'
const THIRTY_MIN_MS = 30 * 60 * 1000

const route = useRoute()
const router = useRouter()

const {
  job: reindexJob,
  isActive: reindexActive,
  progressPct: reindexProgressPct,
  starting: reindexStarting,
  start: startReindexJob,
} = useReindexAcoesJob(() => {
  void load()
})

const reindexStatusLabel = computed(() => {
  switch (reindexJob.value?.status) {
    case 'pending':
      return 'Aguardando início…'
    case 'running':
      return 'Reindexando ações…'
    case 'completed':
      return 'Reindex concluído'
    case 'failed':
      return 'Reindex falhou'
    default:
      return ''
  }
})

async function confirmReindexAcoes(): Promise<void> {
  const result = await Swal.fire({
    icon: 'question',
    title: 'Reindexar ações?',
    html: `
      <p class="small text-muted mb-2">Varre os chunks gravados e atualiza o índice de ações para busca.</p>
      <label class="form-check d-flex justify-content-center gap-2">
        <input id="swal_reindex_force" class="form-check-input" type="checkbox">
        <span class="form-check-label small">Incluir sessões já indexadas</span>
      </label>
    `,
    showCancelButton: true,
    confirmButtonText: 'Iniciar',
    cancelButtonText: 'Cancelar',
    preConfirm: () => {
      const input = document.getElementById('swal_reindex_force') as HTMLInputElement | null
      return { force: input?.checked ?? false }
    },
  })

  if (!result.isConfirmed || !result.value) {
    return
  }

  try {
    await startReindexJob({
      projeto_id: projetoId.value || undefined,
      force: result.value.force,
    })
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Não foi possível iniciar',
      text: getApiErrorMessage(error, 'Erro ao iniciar reindex'),
    })
  }
}

const projetos = ref<Projeto[]>([])
const sessoes = ref<SessaoGravacao[]>([])
const total = ref(0)
const offset = ref(0)
const loading = ref(true)
const removingId = ref<string | null>(null)
const bulkRemoving = ref(false)
const selectedIds = ref<Set<string>>(new Set())

const projetoId = ref('')
const usuarioExternoId = ref('')
const usuarioExternoNome = ref('')
const startedFrom = ref('')
const startedTo = ref('')
const statusFilter = ref<SessaoGravacaoStatus | ''>('')
const comErros = ref(false)
const searchQ = ref('')
const tagFilter = ref('')
const metadadoFilters = ref<Record<string, string>>({})

const projetoSelecionado = computed(() =>
  projetos.value.find((p) => p.id === projetoId.value) ?? null,
)

const camposMetadados = computed(() =>
  (projetoSelecionado.value?.campos_metadados ?? []).filter((c) => !c.omitir),
)

const currentPage = computed(() => Math.floor(offset.value / PAGE_SIZE) + 1)
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)))
const hasSelection = computed(() => selectedIds.value.size > 0)
const canCompare = computed(() => selectedIds.value.size === 2)

const compareLink = computed(() => {
  const ids = [...selectedIds.value]
  if (ids.length !== 2) {
    return null
  }
  return {
    path: '/admin/sessoes/compare',
    query: { left: ids[0], right: ids[1] },
  }
})

const pageMedianDurationMs = computed(() => {
  const durations = sessoes.value
    .map((s) => sessaoDurationMs(s.started_at, s.ended_at, s.status === 'Ativa'))
    .filter((d): d is number => d !== null)
    .sort((a, b) => a - b)
  if (durations.length === 0) return null
  const mid = Math.floor(durations.length / 2)
  return durations.length % 2 === 0
    ? ((durations[mid - 1] ?? 0) + (durations[mid] ?? 0)) / 2
    : (durations[mid] ?? null)
})

watch(projetoId, () => {
  metadadoFilters.value = {}
  for (const campo of camposMetadados.value) {
    metadadoFilters.value[campo.chave] = ''
  }
})

function metadadosLinhas(sessao: SessaoGravacao): { rotulo: string; valor: string }[] {
  return camposMetadados.value
    .map((campo) => ({
      rotulo: campo.rotulo,
      valor: sessao.metadados[campo.chave]?.trim() ?? '',
    }))
    .filter((line) => line.valor !== '')
}

function usuarioLabel(sessao: SessaoGravacao): string {
  if (sessao.usuario_externo_nome) {
    return sessao.usuario_externo_nome
  }
  if (sessao.usuario_externo_id) {
    return sessao.usuario_externo_id
  }
  return '—'
}

function usuarioExternoKey(sessao: SessaoGravacao): string | null {
  const id = sessao.usuario_externo_id?.trim()
  if (id) {
    return `id:${id}`
  }
  const nome = sessao.usuario_externo_nome?.trim()
  if (nome) {
    return `nome:${nome}`
  }
  return null
}

const sharedLoginUsuarioKeys = computed(() => {
  const installIdsByUser = new Map<string, Set<string>>()

  for (const sessao of sessoes.value) {
    if (sessao.status !== 'Ativa') {
      continue
    }
    const userKey = usuarioExternoKey(sessao)
    if (!userKey) {
      continue
    }
    const installId = sessao.metadados?.install_id?.trim()
    if (!installId) {
      continue
    }
    if (!installIdsByUser.has(userKey)) {
      installIdsByUser.set(userKey, new Set())
    }
    installIdsByUser.get(userKey)!.add(installId)
  }

  const suspicious = new Set<string>()
  for (const [userKey, installIds] of installIdsByUser) {
    if (installIds.size > 1) {
      suspicious.add(userKey)
    }
  }
  return suspicious
})

function hasSharedLoginSuspect(sessao: SessaoGravacao): boolean {
  if (sessao.status !== 'Ativa') {
    return false
  }
  const userKey = usuarioExternoKey(sessao)
  return userKey !== null && sharedLoginUsuarioKeys.value.has(userKey)
}

function retencaoDiasFor(sessao: SessaoGravacao): number | null | undefined {
  if (sessao.projeto?.retencao_dias != null) {
    return sessao.projeto.retencao_dias
  }
  return projetos.value.find((p) => p.id === sessao.projeto_id)?.retencao_dias
}

function retentionLabel(sessao: SessaoGravacao): string | null {
  return formatRetentionShort(
    sessao.started_at,
    retencaoDiasFor(sessao),
    30,
    sessao.tags,
  )
}

function isAnomalousDuration(sessao: SessaoGravacao): boolean {
  const d = sessaoDurationMs(sessao.started_at, sessao.ended_at, sessao.status === 'Ativa')
  if (d === null) return false
  if (d > THIRTY_MIN_MS) return true
  const median = pageMedianDurationMs.value
  return median !== null && median > 0 && d > 2 * median
}

function isSelected(id: string): boolean {
  return selectedIds.value.has(id)
}

function toggleSelect(id: string, checked: boolean): void {
  const next = new Set(selectedIds.value)
  if (checked) {
    next.add(id)
  } else {
    next.delete(id)
  }
  selectedIds.value = next
}

function toggleSelectAll(checked: boolean): void {
  if (checked) {
    selectedIds.value = new Set(sessoes.value.map((s) => s.id))
  } else {
    selectedIds.value = new Set()
  }
}

const allVisibleSelected = computed(() =>
  sessoes.value.length > 0 && sessoes.value.every((s) => selectedIds.value.has(s.id)),
)

async function loadProjetos(): Promise<void> {
  projetos.value = await listProjetos()
}

function toDateTimeParam(value: string): string | undefined {
  if (!value.trim()) {
    return undefined
  }

  const [date, time] = value.split('T')
  if (!date || !time) {
    return undefined
  }

  return `${date} ${time.length === 5 ? `${time}:00` : time}`
}

function syncQueryToRoute(): void {
  const query: Record<string, string> = {}
  if (searchQ.value.trim()) query.q = searchQ.value.trim()
  if (tagFilter.value.trim()) query.tag = tagFilter.value.trim()
  if (comErros.value) query.com_erros = '1'
  if (projetoId.value) query.projeto_id = projetoId.value
  if (statusFilter.value) query.status = statusFilter.value
  if (usuarioExternoId.value.trim()) query.usuario_externo_id = usuarioExternoId.value.trim()
  if (usuarioExternoNome.value.trim()) query.usuario_externo_nome = usuarioExternoNome.value.trim()
  if (startedFrom.value) query.started_from = startedFrom.value
  if (startedTo.value) query.started_to = startedTo.value
  for (const [chave, valor] of Object.entries(metadadoFilters.value)) {
    if (valor.trim()) {
      query[`${METADATA_QUERY_PREFIX}${chave}`] = valor.trim()
    }
  }
  const page = Math.floor(offset.value / PAGE_SIZE) + 1
  if (page > 1) query.page = String(page)
  void router.replace({ query })
}

async function load(resetOffset = false): Promise<void> {
  if (resetOffset) {
    offset.value = 0
  }

  loading.value = true
  selectedIds.value = new Set()
  syncQueryToRoute()

  const metadado: Record<string, string> = {}
  for (const [chave, valor] of Object.entries(metadadoFilters.value)) {
    if (valor.trim() !== '') {
      metadado[chave] = valor.trim()
    }
  }

  try {
    const result = await listSessoes({
      projeto_id: projetoId.value || undefined,
      usuario_externo_id: usuarioExternoId.value || undefined,
      usuario_externo_nome: usuarioExternoNome.value || undefined,
      started_from: toDateTimeParam(startedFrom.value),
      started_to: toDateTimeParam(startedTo.value),
      status: statusFilter.value || undefined,
      com_erros: comErros.value || undefined,
      q: searchQ.value.trim() || undefined,
      tag: tagFilter.value.trim() || undefined,
      offset: offset.value,
      limit: PAGE_SIZE,
      metadado: Object.keys(metadado).length > 0 ? metadado : undefined,
    })
    sessoes.value = result.items
    total.value = result.total
    offset.value = result.offset
  } catch (error) {
    sessoes.value = []
    total.value = 0
    await Swal.fire({
      icon: 'error',
      title: 'Erro ao carregar sessões',
      text: getApiErrorMessage(error, 'Não foi possível listar as sessões'),
    })
  } finally {
    loading.value = false
  }
}

function goToPage(page: number): void {
  const clamped = Math.min(Math.max(1, page), totalPages.value)
  offset.value = (clamped - 1) * PAGE_SIZE
  void load()
}

async function remove(sessao: SessaoGravacao): Promise<void> {
  const result = await Swal.fire({
    icon: 'warning',
    title: 'Remover sessão?',
    text: 'Os eventos gravados serão apagados permanentemente.',
    showCancelButton: true,
    confirmButtonText: 'Remover',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#dc3545',
  })

  if (!result.isConfirmed) return

  removingId.value = sessao.id
  try {
    await deleteSessao(sessao.id)
    await load()
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Não foi possível remover',
      text: getApiErrorMessage(error, 'Erro ao remover sessão'),
    })
  } finally {
    removingId.value = null
  }
}

async function removeSelected(): Promise<void> {
  const ids = [...selectedIds.value]
  if (ids.length === 0) return

  const result = await Swal.fire({
    icon: 'warning',
    title: `Remover ${ids.length} sessão(ões)?`,
    text: 'Os eventos gravados serão apagados permanentemente.',
    showCancelButton: true,
    confirmButtonText: 'Remover',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#dc3545',
  })

  if (!result.isConfirmed) return

  bulkRemoving.value = true
  try {
    await deleteSessoesLote(ids)
    await load()
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Não foi possível remover',
      text: getApiErrorMessage(error, 'Erro ao remover sessões'),
    })
  } finally {
    bulkRemoving.value = false
  }
}

function openSessao(id: string): void {
  void router.push(`/admin/sessoes/${id}`)
}

function applyRouteQuery(): void {
  const q = route.query
  comErros.value = typeof q.com_erros === 'string' && (q.com_erros === '1' || q.com_erros === 'true')
  searchQ.value = typeof q.q === 'string' ? q.q : ''
  tagFilter.value = typeof q.tag === 'string' ? q.tag : ''
  projetoId.value = typeof q.projeto_id === 'string' ? q.projeto_id : ''
  statusFilter.value =
    typeof q.status === 'string' && (q.status === 'Ativa' || q.status === 'Encerrada')
      ? q.status
      : ''
  usuarioExternoId.value = typeof q.usuario_externo_id === 'string' ? q.usuario_externo_id : ''
  usuarioExternoNome.value = typeof q.usuario_externo_nome === 'string' ? q.usuario_externo_nome : ''
  startedFrom.value = typeof q.started_from === 'string' ? q.started_from : ''
  startedTo.value = typeof q.started_to === 'string' ? q.started_to : ''
  if (typeof q.page === 'string') {
    const page = Number.parseInt(q.page, 10)
    if (Number.isFinite(page) && page >= 1) {
      offset.value = (page - 1) * PAGE_SIZE
    }
  }
}

function applyMetadadoRouteQuery(): void {
  for (const [key, value] of Object.entries(route.query)) {
    if (typeof value !== 'string' || !key.startsWith(METADATA_QUERY_PREFIX)) {
      continue
    }
    const chave = key.slice(METADATA_QUERY_PREFIX.length)
    if (chave && chave in metadadoFilters.value) {
      metadadoFilters.value[chave] = value
    }
  }
}

onMounted(async () => {
  applyRouteQuery()
  try {
    await loadProjetos()
  } catch {
    projetos.value = []
  }
  applyMetadadoRouteQuery()
  await load()
})
</script>

<template>
  <AdminLayout>
    <template #title>
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <h1 class="h5 mb-1">Sessões gravadas</h1>
          <p class="text-muted small mb-0">Replay de sessões dos sites externos</p>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <RouterLink
            v-if="compareLink"
            :to="compareLink"
            class="btn btn-outline-primary btn-sm"
          >
            <i class="bi bi-layout-split me-1" />
            Comparar selecionadas
          </RouterLink>
          <button
            v-if="hasSelection"
            type="button"
            class="btn btn-outline-danger btn-sm"
            :disabled="bulkRemoving"
            @click="removeSelected"
          >
            Remover selecionadas ({{ selectedIds.size }})
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="reindexActive || reindexStarting"
            @click="confirmReindexAcoes"
          >
            <i class="bi bi-lightning me-1" />
            Reindexar ações
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="loading" @click="load()">
            <i class="bi bi-arrow-clockwise me-1" />
            Atualizar
          </button>
        </div>
      </div>
    </template>

    <div
      v-if="reindexJob"
      class="card shadow-sm border-0 p-3 mb-4"
      :class="{
        'border-warning': reindexJob.status === 'pending' || reindexJob.status === 'running',
        'border-success': reindexJob.status === 'completed',
        'border-danger': reindexJob.status === 'failed',
      }"
    >
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div class="small fw-semibold">{{ reindexStatusLabel }}</div>
        <div class="text-muted small">
          {{ reindexJob.processed }} / {{ reindexJob.total }} sessões
        </div>
      </div>
      <div class="progress mb-2" style="height: 0.5rem">
        <div
          class="progress-bar"
          :class="{
            'progress-bar-striped progress-bar-animated': reindexActive,
            'bg-success': reindexJob.status === 'completed',
            'bg-danger': reindexJob.status === 'failed',
          }"
          role="progressbar"
          :style="{ width: `${reindexProgressPct}%` }"
          :aria-valuenow="reindexProgressPct"
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>
      <div class="text-muted small">
        Atualizadas: {{ reindexJob.atualizadas }} · Ignoradas: {{ reindexJob.ignoradas }} · Erros: {{ reindexJob.erros }}
        <span v-if="reindexJob.error_message"> — {{ reindexJob.error_message }}</span>
      </div>
    </div>

    <form class="card shadow-sm border-0 p-3 mb-4 overflow-hidden sessoes-filters" @submit.prevent="load(true)">
      <div class="row g-3">
        <div class="col-md-4">
          <label class="form-label small" for="filtro_q">Busca</label>
          <input
            id="filtro_q"
            v-model="searchQ"
            type="search"
            class="form-control form-control-sm"
            placeholder="Usuário, URL, ações, tags…"
          >
        </div>
        <div class="col-md-2">
          <label class="form-label small" for="filtro_tag">Tag</label>
          <input
            id="filtro_tag"
            v-model="tagFilter"
            type="text"
            class="form-control form-control-sm"
            placeholder="Filtrar por tag"
          >
        </div>
        <div class="col-md-3">
          <label class="form-label small" for="filtro_projeto">Projeto</label>
          <select id="filtro_projeto" v-model="projetoId" class="form-select form-select-sm">
            <option value="">Todos</option>
            <option v-for="projeto in projetos" :key="projeto.id" :value="projeto.id">
              {{ projeto.nome }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label small" for="filtro_status">Status</label>
          <select id="filtro_status" v-model="statusFilter" class="form-select form-select-sm">
            <option value="">Todos</option>
            <option value="Ativa">Ativa</option>
            <option value="Encerrada">Encerrada</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label small" for="filtro_user_id">Usuário externo (ID)</label>
          <input id="filtro_user_id" v-model="usuarioExternoId" type="text" class="form-control form-control-sm">
        </div>
        <div class="col-md-3">
          <label class="form-label small" for="filtro_user_nome">Usuário externo (nome)</label>
          <input id="filtro_user_nome" v-model="usuarioExternoNome" type="text" class="form-control form-control-sm">
        </div>
        <div class="col-md-3">
          <label class="form-label small" for="filtro_from">Início de</label>
          <input id="filtro_from" v-model="startedFrom" type="datetime-local" class="form-control form-control-sm">
        </div>
        <div class="col-md-3">
          <label class="form-label small" for="filtro_to">Início até</label>
          <input id="filtro_to" v-model="startedTo" type="datetime-local" class="form-control form-control-sm">
        </div>
        <div class="col-md-3 d-flex align-items-end">
          <div class="form-check">
            <input id="filtro_erros" v-model="comErros" class="form-check-input" type="checkbox">
            <label class="form-check-label small" for="filtro_erros">Com erros</label>
          </div>
        </div>
        <div
          v-for="campo in camposMetadados"
          :key="campo.chave"
          class="col-md-3"
        >
          <label class="form-label small" :for="`meta_${campo.chave}`">{{ campo.rotulo }}</label>
          <input
            :id="`meta_${campo.chave}`"
            v-model="metadadoFilters[campo.chave]"
            type="text"
            class="form-control form-control-sm"
          >
        </div>
        <div class="col-12">
          <button type="submit" class="btn btn-sm btn-primary" :disabled="loading">Filtrar</button>
          <span v-if="canCompare" class="text-muted small ms-2">Selecione exatamente 2 sessões para comparar.</span>
        </div>
      </div>
    </form>

    <div v-if="loading" class="text-muted">Carregando sessões…</div>

    <div v-else-if="sessoes.length === 0" class="alert alert-light border">
      Nenhuma sessão encontrada.
    </div>

    <template v-else>
      <div class="card shadow-sm border-0 overflow-hidden">
        <div class="table-responsive sessoes-table-scroll">
          <table class="table table-hover align-middle mb-0 sessoes-table">
          <thead class="table-light">
            <tr>
              <th style="width: 2.5rem">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :checked="allVisibleSelected"
                  aria-label="Selecionar todas"
                  @change="toggleSelectAll(($event.target as HTMLInputElement).checked)"
                >
              </th>
              <th>Projeto</th>
              <th>Usuário</th>
              <th v-if="camposMetadados.length">Metadados</th>
              <th>Tags</th>
              <th>Início</th>
              <th>Duração</th>
              <th>Status</th>
              <th>Retenção</th>
              <th>Bytes</th>
              <th class="text-end">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sessao in sessoes" :key="sessao.id">
              <td @click.stop>
                <input
                  class="form-check-input"
                  type="checkbox"
                  :checked="isSelected(sessao.id)"
                  :aria-label="`Selecionar sessão ${sessao.id}`"
                  @change="toggleSelect(sessao.id, ($event.target as HTMLInputElement).checked)"
                >
              </td>
              <td role="button" @click="openSessao(sessao.id)">{{ sessao.projeto?.nome ?? sessao.projeto_id }}</td>
              <td role="button" @click="openSessao(sessao.id)">
                <div class="fw-medium">{{ usuarioLabel(sessao) }}</div>
                <div class="text-muted small text-truncate" style="max-width: 14rem">{{ sessao.url_inicial || '—' }}</div>
                <span
                  v-if="hasSharedLoginSuspect(sessao)"
                  class="badge text-bg-warning mt-1"
                  title="Mesmo usuário com sessões ativas em install_id diferentes nesta lista"
                >
                  Vários aparelhos
                </span>
              </td>
              <td
                v-if="camposMetadados.length"
                class="small sessoes-table__metadados"
                role="button"
                @click="openSessao(sessao.id)"
              >
                <template v-if="metadadosLinhas(sessao).length">
                  <div
                    v-for="line in metadadosLinhas(sessao)"
                    :key="line.rotulo"
                    class="text-truncate"
                    :title="`${line.rotulo}: ${line.valor}`"
                  >
                    <span class="text-muted">{{ line.rotulo }}:</span>
                    {{ line.valor }}
                  </div>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td role="button" @click="openSessao(sessao.id)">
                <div class="d-flex flex-wrap gap-1">
                  <span
                    v-for="tag in sessao.tags ?? []"
                    :key="tag"
                    class="badge text-bg-primary"
                  >
                    {{ tag }}
                  </span>
                  <span v-if="!(sessao.tags ?? []).length" class="text-muted small">—</span>
                </div>
              </td>
              <td class="text-nowrap" role="button" @click="openSessao(sessao.id)">
                {{ formatDateTime(sessao.started_at) ?? '—' }}
              </td>
              <td class="text-nowrap small" role="button" @click="openSessao(sessao.id)">
                {{ formatSessaoDuration(sessao.started_at, sessao.ended_at, sessao.status === 'Ativa') }}
                <span
                  v-if="isAnomalousDuration(sessao)"
                  class="badge text-bg-warning ms-1"
                  title="Duração anômala (>30min ou >2× mediana da página)"
                >
                  longa
                </span>
              </td>
              <td role="button" @click="openSessao(sessao.id)">
                <span class="badge" :class="sessao.status === 'Ativa' ? 'text-bg-success' : 'text-bg-secondary'">
                  {{ sessao.status }}
                </span>
                <span
                  v-if="(sessao.error_events_count ?? 0) > 0"
                  class="badge ms-1"
                  :class="(sessao.error_events_count ?? 0) >= 5 ? 'text-bg-danger' : 'text-bg-warning'"
                  title="Erros na sessão"
                >
                  {{ sessao.error_events_count }} err
                </span>
              </td>
              <td class="small text-muted text-nowrap" role="button" @click="openSessao(sessao.id)">
                {{ retentionLabel(sessao) ?? '—' }}
              </td>
              <td class="small text-muted" role="button" @click="openSessao(sessao.id)">
                {{ formatSessaoBytes(sessao.events_bytes) }}
              </td>
              <td class="text-end text-nowrap" @click.stop>
                <RouterLink
                  :to="`/admin/sessoes/${sessao.id}`"
                  class="btn btn-sm btn-outline-primary me-1"
                >
                  Abrir
                </RouterLink>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  :disabled="removingId === sessao.id"
                  @click="remove(sessao)"
                >
                  Remover
                </button>
              </td>
            </tr>
          </tbody>
          </table>
        </div>
      </div>

      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
        <span class="text-muted small">
          {{ total }} sessão(ões) · página {{ currentPage }} de {{ totalPages }}
        </span>
        <nav aria-label="Paginação de sessões">
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: currentPage <= 1 }">
              <button type="button" class="page-link" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">
                Anterior
              </button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
              <button type="button" class="page-link" :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">
                Próxima
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </template>
  </AdminLayout>
</template>

<style scoped>
.sessoes-table-scroll {
  max-width: 100%;
}

.sessoes-table {
  width: 100%;
}

.sessoes-table__metadados {
  max-width: 14rem;
}

.sessoes-filters :deep(input[type='datetime-local']) {
  min-width: 0;
}
</style>
